import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const LOGO_URL = "/images/area-logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // verifying, success, error

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setStatus('error');
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (response.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="verify-email-page">
      <div className="w-full max-w-md text-center">
        <Link to="/"><img src={LOGO_URL} alt="AREA" className="h-14 w-auto mx-auto mb-6" /></Link>

        <Card className="border border-slate-200 shadow-lg">
          <CardContent className="p-8">
            {status === 'verifying' && (
              <div className="py-4">
                <Loader2 className="w-12 h-12 text-[#3D4F7A] mx-auto mb-4 animate-spin" />
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Verifying your email</h2>
                <p className="text-sm text-slate-600">Please wait...</p>
              </div>
            )}
            {status === 'success' && (
              <div className="py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Email verified!</h2>
                <p className="text-sm text-slate-600 mb-6">Your email has been verified successfully.</p>
                <Link to="/dashboard">
                  <Button className="w-full bg-[#3D4F7A] hover:bg-[#2A3755]" data-testid="go-to-dashboard-btn">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            )}
            {status === 'error' && (
              <div className="py-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Verification failed</h2>
                <p className="text-sm text-slate-600 mb-6">
                  This verification link is invalid or has expired. Please request a new one from your dashboard.
                </p>
                <Link to="/dashboard">
                  <Button className="w-full bg-[#3D4F7A] hover:bg-[#2A3755]" data-testid="go-to-dashboard-btn">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
